/**
 * (c) copyright 2012-2022 mgm technology partners GmbH.
 * This software, the underlying source code and other artifacts are protected by copyright.
 * All rights, in particular the right to use, reproduce, publish and edit are reserved.
 * A simple right of use (license) can be acquired for use, duplication, publication, editing etc..
 * Requests for this can be made at A12-license@mgm-tp.com or other official channels of the copyright holder.
 */
/**
 * @packageDocumentation
 * @module models
 */
import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";
import { FormModel } from "@com.mgmtp.a12.formengine/formengine-core/lib/models";

import { FormModelPath, getModelPathElementName } from "./form-model-path";
import { ModelVisitor, ModelWalker, VisitProcess } from "./form-model-walker";

/**
 * @internal
 * @ignore
 */
export namespace FormModelUtils {
	type MatchGuard = <T extends object>(element: T | undefined) => T | undefined;

	/** Returns the element if it matches the path segment or undefined otherwise. */
	function createMatchGuard(name: string): MatchGuard {
		return function matchGuard(element) {
			if (element === undefined) {
				return undefined;
			}

			// Using modelPathRepresentation is slow in comparison to use `element.name || element.id`
			const elementName = getModelPathElementName(element);
			return elementName === name ? element : undefined;
		};
	}

	/** @internal */
	export function getChild(formModelElement: object, name: string): object | undefined {
		const matchGuard = createMatchGuard(name);

		if (FormModel.isInstance(formModelElement)) {
			return (
				formModelElement.content.screens.find(matchGuard) ||
				matchGuard(formModelElement.content.subHeaderBox) ||
				matchGuard(formModelElement.content.footerBox)
			);
		} else if (FormModel.Screen.isInstance(formModelElement)) {
			return (
				formModelElement.screenElements.find(matchGuard) ||
				matchGuard(formModelElement.subHeaderBox) ||
				matchGuard(formModelElement.footerBox)
			);
		} else if (isHeaderFooterTypeInstance(formModelElement)) {
			return (
				formModelElement.majorButtons?.button?.find(matchGuard) ||
				formModelElement.minorButtons?.button?.find(matchGuard)
			);
		} else if (FormModel.ButtonPanel.isInstance(formModelElement)) {
			return formModelElement.button?.find(matchGuard);
		} else if (FormModel.Section.isInstance(formModelElement)) {
			return formModelElement.screenElements?.find(matchGuard);
		} else if (FormModel.MultiColumnSection.isInstance(formModelElement)) {
			return formModelElement.screenElements?.find(matchGuard);
		} else if (FormModel.ControlGrid.isInstance(formModelElement)) {
			return formModelElement.row?.find(matchGuard);
		} else if (FormModel.Row.isInstance(formModelElement)) {
			return formModelElement.cell?.find(matchGuard);
		} else if (FormModel.InlineRepeat.isInstance(formModelElement)) {
			return formModelElement.repeatOverviewColumn?.find(matchGuard);
		} else if (FormModel.DetachedRepeat.isInstance(formModelElement)) {
			return formModelElement.repeatOverviewColumn?.find(matchGuard) || matchGuard(formModelElement.detailScreen);
		} else if (FormModel.EmbeddedRepeat.isInstance(formModelElement)) {
			return formModelElement.repeatOverviewColumn?.find(matchGuard) || matchGuard(formModelElement.controlGrid);
		} else {
			return undefined;
		}
	}

	export function isHeaderFooterTypeInstance(
		element: object | FormModel.HeaderFooterType
	): element is FormModel.HeaderFooterType {
		return "minorButtons" in element || "majorButtons" in element;
	}

	export function isIdNamedInstance(element: object | FormModel.IdNamed): element is FormModel.IdNamed {
		return "id" in element && "name" in element;
	}

	export function isAnnotatedInstance(element: object | FormModel.IdNamed): element is FormModel.Annotated {
		return "annotations" in element;
	}

	/** @internal */
	export function findPathElementsByFormModelPath(currentElement: object, targetPath: ModelPath): object[] {
		const result = [currentElement];

		for (let i = 0; i < targetPath.length; i++) {
			const element = getChild(result[i], targetPath[i].elementName);
			if (element === undefined) {
				throw new Error(`Invalid Form Model Path: ${ModelPath.toString(targetPath)}`);
			}

			result.push(element);
		}

		return result;
	}

	/** @internal */
	export interface EditableElement {
		readonly formModelElement: FormModel.FieldBasedInputType;
		/**
		 * The path from a given parent, including the name of the parent
		 */
		readonly relativeFormModelPath: ModelPath;
	}

	/** @internal */
	export namespace EditableElement {
		/**
		 * @internal
		 *
		 * Finds all editable elements.
		 *
		 * @param parent: The parent from which the search starts
		 * @param documentModelPath: An optional DocumentModel path to restrict the search
		 */
		/** @internal */
		export function find(
			parent: object,
			documentModelPath: ModelPath,
			firstOccurrence?: boolean
		): EditableElement[] {
			const editableElementsCollector = new EditableFieldsCollector(documentModelPath, firstOccurrence);
			new ModelWalker(editableElementsCollector).acceptGeneric(parent);
			return editableElementsCollector.allFields;
		}

		class EditableFieldsCollector implements ModelVisitor {
			public allFields: EditableElement[] = [];
			private elementStack: object[] = [];
			private readonly formModelPathStack: ModelPath[] = [];

			constructor(
				private readonly documentModelPath: ModelPath,
				private readonly firstOccurrence?: boolean
			) {}

			addIfMatchesAndSignalProcess(element: FormModel.FieldBasedInputType): boolean {
				if (
					this.documentModelPath === undefined ||
					ModelPath.contains(this.documentModelPath, element.elementPath)
				) {
					this.allFields.push({
						formModelElement: element,
						relativeFormModelPath: peek(this.formModelPathStack)!
					});
					if (this.firstOccurrence) {
						return true;
					}
				}
				return false;
			}

			isFieldOverviewColumnOfInlineRepeat(
				repeatColumn: FormModel.RepeatOverviewColumn
			): repeatColumn is FormModel.FieldOverviewColumn {
				return (
					FormModel.FieldOverviewColumn.isInstance(repeatColumn) &&
					FormModel.InlineRepeat.isInstance(this.elementStack[this.elementStack.length - 2])
				);
			}

			visitControl(control: FormModel.Control): VisitProcess {
				const stopTraversal = this.addIfMatchesAndSignalProcess(control);
				return stopTraversal ? VisitProcess.Stop : VisitProcess.ContinueTraversal;
			}

			visitRepeatOverviewColumn(repeatColumn: FormModel.RepeatOverviewColumn): VisitProcess {
				if (this.isFieldOverviewColumnOfInlineRepeat(repeatColumn)) {
					const stopTraversal = this.addIfMatchesAndSignalProcess(repeatColumn);
					if (stopTraversal) {
						return VisitProcess.Stop;
					}
				}
				return VisitProcess.ContinueTraversal;
			}

			enter(elementStack: object[]): void {
				this.elementStack = elementStack;
				const currentElementPath = FormModelPath.extend(
					peek(this.formModelPathStack) || [],
					peek(elementStack)!
				);
				this.formModelPathStack.push(currentElementPath);
			}

			leave() {
				this.formModelPathStack.pop();
			}
		}
	}
}

export function peek<T>(list: T[]): T | undefined {
	if (list.length === 0) {
		return undefined;
	}
	return list[list.length - 1];
}

export type AnnotatedInput = FormModel.Annotated & FormModel.FieldBasedInput;

export class FormModelComponentCollector implements ModelVisitor {
	private readonly annotatedInputs: AnnotatedInput[] = [];
	private readonly repeats: FormModel.Repeat[] = [];

	visitControl(control: FormModel.Control): VisitProcess {
		this.annotatedInputs.push(control);
		return VisitProcess.ContinueTraversal;
	}

	visitRepeatOverviewColumn(repeatColumn: FormModel.RepeatOverviewColumn): VisitProcess {
		if (FormModel.FieldOverviewColumn.isInstance(repeatColumn)) {
			this.annotatedInputs.push(repeatColumn);
		}
		return VisitProcess.ContinueTraversal;
	}

	visitDetachedRepeat(repeat: FormModel.DetachedRepeat): VisitProcess {
		this.repeats.push(repeat);
		return VisitProcess.ContinueTraversal;
	}

	visitEmbeddedRepeat(repeat: FormModel.EmbeddedRepeat): VisitProcess {
		this.repeats.push(repeat);
		return VisitProcess.ContinueTraversal;
	}

	visitInlineRepeat(repeat: FormModel.InlineRepeat): VisitProcess {
		this.repeats.push(repeat);
		return VisitProcess.ContinueTraversal;
	}

	getAnnotatedInputs(): AnnotatedInput[] {
		return this.annotatedInputs;
	}

	getRepeats(): FormModel.Repeat[] {
		return this.repeats;
	}
}
